import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    DollarSign, Building, AlertCircle, CheckCircle, ArrowLeft
} from 'lucide-react'
import { useLanguageStore } from '../../lib/store'
import { formatCurrency } from '../../lib/utils'
import { walletApi } from '../../lib/api'

export default function WithdrawalPage() {
    useTranslation()
    const { language } = useLanguageStore()

    const [amount, setAmount] = useState('')
    const [bankName, setBankName] = useState('')
    const [accountNumber, setAccountNumber] = useState('')
    const [accountHolder, setAccountHolder] = useState('')
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const [error, setError] = useState('')

    // Mock data
    const walletBalance = 8500000
    const minWithdrawal = 500000
    const maxWithdrawal = walletBalance

    const quickAmounts = [1000000, 2000000, 5000000, walletBalance]

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const amountNum = parseInt(amount)

        if (amountNum < minWithdrawal) {
            setError(language === 'en' ? `Minimum withdrawal is ${formatCurrency(minWithdrawal, 'VND')}` : `Số tiền tối thiểu là ${formatCurrency(minWithdrawal, 'VND')}`)
            return
        }

        if (amountNum > maxWithdrawal) {
            setError(language === 'en' ? 'Amount exceeds balance' : 'Số tiền vượt quá số dư')
            return
        }

        if (!bankName || !accountNumber || !accountHolder) {
            setError(language === 'en' ? 'Please fill all bank details' : 'Vui lòng điền đầy đủ thông tin ngân hàng')
            return
        }

        setLoading(true)
        setError('')

        try {
            const response = await walletApi.requestWithdrawal({
                amount: amountNum,
                bankInfo: { bankName, accountNumber, accountHolder },
            })

            if (response.error) {
                throw new Error(response.error)
            }

            setSuccess(true)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Withdrawal request failed')
        } finally {
            setLoading(false)
        }
    }

    if (success) {
        return (
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="container mx-auto px-4">
                    <div className="max-w-md mx-auto">
                        <div className="card text-center py-12">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-green-600" />
                            </div>
                            <h2 className="text-2xl font-bold mb-2">
                                {language === 'en' ? 'Request Submitted!' : 'Đã Gửi Yêu Cầu!'}
                            </h2>
                            <p className="text-gray-600 mb-2">
                                {language === 'en'
                                    ? `Withdrawal of ${formatCurrency(parseInt(amount), 'VND')} is pending approval.`
                                    : `Yêu cầu rút ${formatCurrency(parseInt(amount), 'VND')} đang chờ phê duyệt.`
                                }
                            </p>
                            <p className="text-sm text-gray-500 mb-6">
                                {language === 'en'
                                    ? 'You will receive the funds within 1-3 business days after approval.'
                                    : 'Bạn sẽ nhận được tiền trong 1-3 ngày làm việc sau khi được duyệt.'
                                }
                            </p>
                            <Link
                                to="/owner"
                                className="btn bg-primary-600 text-white hover:bg-primary-700"
                            >
                                {language === 'en' ? 'Back to Dashboard' : 'Về Bảng Điều Khiển'}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="container mx-auto px-4">
                <div className="max-w-md mx-auto">
                    <Link to="/owner" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        {language === 'en' ? 'Back' : 'Quay lại'}
                    </Link>

                    <h1 className="text-3xl font-bold mb-2">{language === 'en' ? 'Withdraw Funds' : 'Rút Tiền'}</h1>
                    <p className="text-gray-600 mb-8">
                        {language === 'en' ? 'Transfer your earnings to your bank account' : 'Chuyển thu nhập về tài khoản ngân hàng'}
                    </p>

                    {/* Balance Card */}
                    <div className="card bg-gradient-to-r from-green-500 to-green-600 text-white mb-6">
                        <p className="opacity-80 text-sm">{language === 'en' ? 'Available Balance' : 'Số dư khả dụng'}</p>
                        <p className="text-3xl font-bold mt-1">{formatCurrency(walletBalance, 'VND')}</p>
                    </div>

                    <form onSubmit={handleSubmit} className="card">
                        {/* Amount */}
                        <div className="mb-6">
                            <label className="block text-sm font-medium mb-2">
                                {language === 'en' ? 'Amount' : 'Số tiền rút'} *
                            </label>
                            <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    placeholder="0"
                                    className="input pl-10 text-xl font-bold"
                                />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                {language === 'en' ? 'Minimum' : 'Tối thiểu'}: {formatCurrency(minWithdrawal, 'VND')}
                            </p>

                            {/* Quick Amount Buttons */}
                            <div className="flex gap-2 mt-3">
                                {quickAmounts.map((amt) => (
                                    <button
                                        key={amt}
                                        type="button"
                                        onClick={() => setAmount(amt.toString())}
                                        className={`flex-1 py-2 text-sm rounded-lg border transition ${parseInt(amount) === amt ? 'border-primary-500 bg-primary-50 text-primary-600' : 'hover:bg-gray-50'
                                            }`}
                                    >
                                        {amt === walletBalance ? (language === 'en' ? 'All' : 'Tất cả') : `${(amt / 1000000).toFixed(0)}M`}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Bank Info */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">
                                <Building className="w-4 h-4 inline mr-1" />
                                {language === 'en' ? 'Bank Name' : 'Ngân hàng'} *
                            </label>
                            <select
                                value={bankName}
                                onChange={(e) => setBankName(e.target.value)}
                                className="input"
                            >
                                <option value="">{language === 'en' ? 'Select bank' : 'Chọn ngân hàng'}</option>
                                <option value="Vietcombank">Vietcombank</option>
                                <option value="Techcombank">Techcombank</option>
                                <option value="BIDV">BIDV</option>
                                <option value="VietinBank">VietinBank</option>
                                <option value="MBBank">MBBank</option>
                                <option value="VPBank">VPBank</option>
                                <option value="ACB">ACB</option>
                                <option value="TPBank">TPBank</option>
                                <option value="Sacombank">Sacombank</option>
                            </select>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">
                                {language === 'en' ? 'Account Number' : 'Số tài khoản'} *
                            </label>
                            <input
                                type="text"
                                value={accountNumber}
                                onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                                placeholder="0123456789"
                                className="input"
                            />
                        </div>

                        <div className="mb-6">
                            <label className="block text-sm font-medium mb-2">
                                {language === 'en' ? 'Account Holder Name' : 'Tên chủ tài khoản'} *
                            </label>
                            <input
                                type="text"
                                value={accountHolder}
                                onChange={(e) => setAccountHolder(e.target.value.toUpperCase())}
                                placeholder="NGUYEN VAN A"
                                className="input uppercase"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                {language === 'en' ? 'Must match the name on your bank account' : 'Phải trùng với tên trên tài khoản ngân hàng'}
                            </p>
                        </div>

                        {/* Notice */}
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
                            <div className="flex gap-2">
                                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-yellow-800">
                                    <p className="font-medium mb-1">{language === 'en' ? 'Important' : 'Lưu ý'}</p>
                                    <ul className="list-disc list-inside space-y-1 text-xs">
                                        <li>{language === 'en' ? 'Withdrawals are processed within 1-3 business days' : 'Rút tiền được xử lý trong 1-3 ngày làm việc'}</li>
                                        <li>{language === 'en' ? 'Make sure your bank info is correct' : 'Đảm bảo thông tin ngân hàng chính xác'}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading || !amount || !bankName || !accountNumber || !accountHolder}
                            className="w-full py-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white rounded-lg font-bold text-lg transition"
                        >
                            {loading
                                ? (language === 'en' ? 'Processing...' : 'Đang xử lý...')
                                : (language === 'en' ? 'Submit Withdrawal Request' : 'Gửi Yêu Cầu Rút Tiền')
                            }
                        </button>
                    </form>
                </div>
            </div>
        </div>
    )
}
